/// <reference types="react" />
import React, { useEffect, useState, useMemo, useRef } from 'react';
import Tree from 'react-d3-tree';
import './App.css';
import { DuplicateCandidate, PendingSave } from './components/DuplicateSuggestions';
import FanChart from './components/FanChart';
import IndividualModal from './components/IndividualModal';
import './FamilyTreeIndividualBox.css';
import { useHomePersonId } from './utils/useHomePersonId';

interface Individual {
  id: string;
  first_name?: string;
  middle_names?: string;
  surname?: string;
  is_placeholder?: boolean;
  gender?: string;
  dob?: string;
  status?: string;
  death_dob?: string;
  metadata?: { [key: string]: any };
  is_progenitor?: boolean;
  progenitor_line_name?: string;
  is_ghost?: boolean;
  // New DNA/relationship fields
  shared_dna_cm?: number;
  longest_segment_cm?: number;
  number_of_segments?: number;
  percent_shared_dna?: number;
  predicted_relationship?: string;
  actual_relationship?: string;
  confidence_score?: number;
}

interface Relationship {
  id: string;
  from_id: string;
  to_id: string;
  type: string;
  sources?: string[];
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const FamilyTreePage: React.FC = () => {
  const homePersonIdForDna = useHomePersonId();
  const [individuals, setIndividuals] = useState<Individual[]>([]);
  const [relationships, setRelationships] = useState<Relationship[]>([]);
  const [selectedChildId, setSelectedChildId] = useState('');
  const [selectedProgenitorId, setSelectedProgenitorId] = useState<string | null>(null);
  const [dnaMatchModalOpen, setDnaMatchModalOpen] = useState(false);
  const [dnaMatchData, setDnaMatchData] = useState<any>(null);
  const [dnaMatchLoading, setDnaMatchLoading] = useState(false);
  const [dnaMatchError, setDnaMatchError] = useState<string | null>(null);
  const [personModalOpen, setPersonModalOpen] = useState(false);
  const [personModalData, setPersonModalData] = useState<Individual | null>(null);
  const [editIndividualModalOpen, setEditIndividualModalOpen] = useState(false);
  const [editIndividual, setEditIndividual] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [modalIndividual, setModalIndividual] = useState<any>({});
  const [duplicateCandidates, setDuplicateCandidates] = useState<DuplicateCandidate[]>([]);
  // Last add-form submission, replayed by "create anyway"
  const pendingSaveRef = useRef<PendingSave | null>(null);
  const [potentialParents, setPotentialParents] = useState<any[]>([]);
  const [potentialChildren, setPotentialChildren] = useState<any[]>([]);
  const [potentialParentsMap, setPotentialParentsMap] = useState<{[id: string]: any[]}>({});
  const [progenitorIndex, setProgenitorIndex] = useState<any>(null);
  // Line name -> colour, assigned by the backend so every page agrees
  const [lineColors, setLineColors] = useState<{ [line: string]: string }>({});
  const [rebuildLoading, setRebuildLoading] = useState(false);
  const [rebuildMessage, setRebuildMessage] = useState<string | null>(null);
  const treeContainerRef = useRef<HTMLDivElement>(null);
  const [treeZoom, setTreeZoom] = useState(1);
  const [treeTranslate, setTreeTranslate] = useState<{ x: number; y: number }>({ x: 200, y: 500 });
  const [treeLayout, setTreeLayout] = useState<'horizontal' | 'vertical' | 'fan'>('horizontal');
  const [progenitorDnaSummary, setProgenitorDnaSummary] = useState<{ [progenitorId: string]: { matchNames: string[], totalCm: number } }>({});
  const [dnaModalShowOnlyMultiPerson, setDnaModalShowOnlyMultiPerson] = useState(false);

  useEffect(() => {
    fetchIndividuals();
    // Fetch progenitor index on mount
    fetch(`${API_URL}/progenitor_index`).then(r => r.json()).then(setProgenitorIndex);
  }, []);

  useEffect(() => {
    if (individuals.length > 0) fetchRelationships();
    // Progenitor flags or line names may have changed with the individuals
    fetch(`${API_URL}/progenitor_lines`).then(r => r.json()).then(setLineColors).catch(() => {});
  }, [individuals]);

  // Batch fetch potential parents for all individuals (one state update)
  useEffect(() => {
    if (individuals.length === 0) return;
    const abort = { current: false };
    Promise.all(
      individuals.map(ind =>
        fetch(`${API_URL}/individuals/${ind.id}/potential_parents`).then(r => r.json()).then(data => ({ id: ind.id, data }))
      )
    ).then(results => {
      if (abort.current) return;
      const next: { [id: string]: any[] } = {};
      results.forEach(({ id, data }) => { next[id] = data; });
      setPotentialParentsMap(prev => ({ ...prev, ...next }));
    });
    return () => { abort.current = true; };
  }, [individuals]);

  // Fetch DNA match data for all progenitors on tree load
  useEffect(() => {
    if (!individuals.length) return;
    const progenitors = individuals.filter(i => i.is_progenitor);
    if (progenitors.length === 0) return;
    const fetchAll = async () => {
      const summary: { [progenitorId: string]: { matchNames: string[], totalCm: number } } = {};
      await Promise.all(progenitors.map(async (prog) => {
        try {
          const res = await fetch(`${API_URL}/progenitor/${prog.id}/dna_matches?home_id=${encodeURIComponent(selectedChildId || homePersonIdForDna)}`);
          if (!res.ok) return;
          const data = await res.json();
          // Collect match names and total cM
          const matchNames = (data.matches || []).map((m: any) => m.match_name);
          // Sum all cM for this progenitor
          let totalCm = 0;
          (data.matches || []).forEach((m: any) => {
            totalCm += (m.segments || []).reduce((sum: number, seg: any) => sum + (seg.centimorgans || 0), 0);
          });
          summary[prog.id] = { matchNames, totalCm };
        } catch (e) {
          // Ignore errors for now
        }
      }));
      setProgenitorDnaSummary(summary);
    };
    fetchAll();
  }, [individuals, selectedChildId, homePersonIdForDna]);

  const fetchIndividuals = () => {
    fetch(`${API_URL}/individuals`)
      .then((res) => res.json())
      .then((data) => setIndividuals(data))
      .catch(() => {});
  };

  const fetchRelationships = () => {
    fetch(`${API_URL}/relationships`)
      .then((res) => res.json())
      .then((data) => setRelationships(data))
      .catch(() => {});
  };

  // Helper: Get all parents (confirmed and potential) for a given individual
  const getAllParents = (childId: string) => {
    // All parent relationships (for now, show all until we can properly identify non-bio parents)
    const parentRels = relationships.filter(
      (rel: Relationship) => rel.to_id === childId && (rel.type === 'Bio Father' || rel.type === 'Bio Mother')
    );
    const confirmedParents = parentRels.map(rel => individuals.find(ind => ind.id === rel.from_id)).filter(Boolean) as Individual[];
    
    // Deduplicate confirmed parents by ID
    const uniqueConfirmedParents = confirmedParents.filter((parent, index, self) => 
      index === self.findIndex(p => p.id === parent.id)
    );
    
    // Check for other parent types (adoptive, step, etc.)
    // For now, since GEDCOM parser only creates Bio Father/Mother, we'll check for any non-bio relationships
    const otherParentRels = relationships.filter(
      (rel: Relationship) => rel.to_id === childId && 
      (rel.type.includes('Father') || rel.type.includes('Mother')) && 
      !rel.type.includes('Bio')
    );
    const hasOtherParents = otherParentRels.length > 0;
    
    // Potential parents
    const potentialParents = (potentialParentsMap[childId] || []).map(p => p.individual);
    // Remove duplicates (if any)
    const all = [...uniqueConfirmedParents];
    potentialParents.forEach(p => {
      if (!all.find(cp => cp.id === p.id)) all.push(p);
    });
    return { 
      confirmedParents: uniqueConfirmedParents, 
      potentialParents, 
      all,
      hasOtherParents,
      otherParentCount: otherParentRels.length
    };
  };

  // Predicates extracted to satisfy no-loop-func (no functions created inside loops)
  const isParentOfChild = (rel: Relationship, childId: string) =>
    rel.to_id === childId && (rel.type === 'Bio Father' || rel.type === 'Bio Mother');
  const isFromHome = (r: Relationship, homeId: string) => r.from_id === homeId;
  const isBioFather = (r: Relationship) => r.type === 'Bio Father';
  const isBioMother = (r: Relationship) => r.type === 'Bio Mother';

  // Helper to determine relationship path from home person
  const getRelationshipPath = (personId: string, homePersonId: string): string[] => {
    if (personId === homePersonId) return ['self'];
    
    const path: string[] = [];
    let currentId = personId;
    const visited = new Set<string>();
    
    while (currentId && currentId !== homePersonId && !visited.has(currentId)) {
      visited.add(currentId);
      
      const parentRels: Relationship[] = [];
      for (let i = 0; i < relationships.length; i++) {
        const rel = relationships[i];
        if (isParentOfChild(rel, currentId)) parentRels.push(rel);
      }
      
      if (parentRels.length === 0) break;
      
      let homePersonParentRel: Relationship | undefined;
      for (let i = 0; i < parentRels.length; i++) {
        if (isFromHome(parentRels[i], homePersonId)) {
          homePersonParentRel = parentRels[i];
          break;
        }
      }
      if (homePersonParentRel) {
        path.unshift(homePersonParentRel.type === 'Bio Father' ? 'father' : 'mother');
        break;
      }
      
      let fatherRel: Relationship | undefined;
      let motherRel: Relationship | undefined;
      for (let i = 0; i < parentRels.length; i++) {
        if (isBioFather(parentRels[i])) fatherRel = parentRels[i];
        if (isBioMother(parentRels[i])) motherRel = parentRels[i];
      }
      
      if (fatherRel) {
        path.unshift('father');
        currentId = fatherRel.from_id;
      } else if (motherRel) {
        path.unshift('mother');
        currentId = motherRel.from_id;
      } else {
        break;
      }
    }
    
    return path;
  };

  // Helper to get color based on relationship path
  const getRelationshipColor = (personId: string, homePersonId: string): string => {
    const path = getRelationshipPath(personId, homePersonId);
    
    if (path.length === 0 || path[0] === 'self') {
      return '#e0e0e0'; // Light gray for home person
    }
    
    // Count paternal and maternal steps
    let paternalSteps = 0;
    let maternalSteps = 0;
    
    for (const step of path) {
      if (step === 'father') paternalSteps++;
      else if (step === 'mother') maternalSteps++;
    }
    
    // Determine base color (blue for paternal, pink for maternal)
    // Check if the path starts with 'mother' to determine maternal line
    const isMaternal = path[0] === 'mother';
    const generation = Math.max(paternalSteps, maternalSteps);
    
    if (isMaternal) {
      // Pink shades for maternal line
      const baseHue = 320; // Pink
      const saturation = Math.max(30, 100 - generation * 12);
      const lightness = Math.max(40, 85 - generation * 8);
      return `hsl(${baseHue}, ${saturation}%, ${lightness}%)`;
    } else {
      // Blue shades for paternal line
      const baseHue = 220; // Blue
      const saturation = Math.max(30, 100 - generation * 12);
      const lightness = Math.max(40, 85 - generation * 8);
      return `hsl(${baseHue}, ${saturation}%, ${lightness}%)`;
    }
  };

  // Helper: for a given individual, find all progenitor lines they descend from (traverse both confirmed and potential parents)
  function getDescendantProgenitorLines(individualId: string): string[] {
    if (!progenitorIndex || !progenitorIndex[individualId]) return [];
    // Map progenitor IDs to line names
    const entry = progenitorIndex[individualId];
    const lines = [...(entry.progenitors || []), ...(entry.possible_progenitors || [])]
      .map((pid: string) => {
        const ind = individuals.find((i) => i.id === pid);
        return ind && ind.progenitor_line_name ? ind.progenitor_line_name : null;
      })
      .filter((line): line is string => Boolean(line));
    return Array.from(new Set(lines));
  }

  // Utility: assign a unique color for each progenitor line name
  function getProgenitorColor(line: string | undefined) {
    return (line && lineColors[line]) || '#888';
  }
  // Build a map of individual id to their parents (confirmed and potential)
  const parentMap: { [childId: string]: string[] } = {};
  relationships.forEach((rel: Relationship) => {
    if ((rel.type === 'Bio Father' || rel.type === 'Bio Mother')) {
      if (!parentMap[rel.to_id]) parentMap[rel.to_id] = [];
      parentMap[rel.to_id].push(rel.from_id);
    }
  });
  // Add potential parents from potentialParentsMap
  Object.entries(potentialParentsMap).forEach(([childId, potentialArr]) => {
    if (!parentMap[childId]) parentMap[childId] = [];
    potentialArr.forEach((p: any) => {
      if (p && p.individual && !parentMap[childId].includes(p.individual.id)) {
        parentMap[childId].push(p.individual.id);
      }
    });
  });
  // Helper: assign a class name for each link based on the child's first progenitor line
  function getLinkClassName(childDescendantLines: string[]): string {
    if (childDescendantLines && childDescendantLines.length > 0) {
      return `progenitor-link-${childDescendantLines[0].replace(/[^a-zA-Z0-9]/g, '')}`;
    }
    return 'progenitor-link-default';
  }

  // Update buildAncestorTree to include potential parents
  const buildAncestorTree = (individualId: string, depth = 1, maxDepth = 8, visited = new Set(), linkType = 'confirmed'): any => {
    if (!individualId || depth > maxDepth || visited.has(individualId)) return null;
    visited.add(individualId);
    const ind = individuals.find(i => i.id === individualId);
    if (!ind) return null;
    const { confirmedParents, potentialParents, hasOtherParents, otherParentCount } = getAllParents(individualId);
    const children: any[] = [];
    // Confirmed parents (solid link)
    confirmedParents.forEach(parent => {
      const tree = buildAncestorTree(parent.id, depth + 1, maxDepth, new Set(visited), 'confirmed');
      if (tree) children.push({ ...tree, __linkType: 'confirmed' });
    });
    // Potential parents (dashed link)
    potentialParents.forEach(parent => {
      // Avoid duplicating confirmed parents
      if (!confirmedParents.find(cp => cp.id === parent.id)) {
        const tree = buildAncestorTree(parent.id, depth + 1, maxDepth, new Set(visited), 'potential');
        if (tree) children.push({ ...tree, __linkType: 'potential' });
      }
    });
    const descendantLines = getDescendantProgenitorLines(individualId);
    return {
      name: [ind.first_name, ind.middle_names, ind.surname].filter(Boolean).join(' ') || 'Unknown',
      attributes: {
        gender: ind.gender || 'unknown',
        dob: ind.dob || '',
        status: ind.status || '',
        descendantLines,
        is_ghost: ind.is_ghost,
        hasOtherParents,
        otherParentCount,
      },
      children: children.length > 0 ? children : undefined,
      id: ind.id,
      __linkClassName: getLinkClassName(descendantLines),
      __linkType: linkType,
      is_ghost: ind.is_ghost,
    };
  };

  // Update treeData to use new buildAncestorTree
  const treeData = useMemo(() => {
    if (!selectedChildId) return null;
    const root = buildAncestorTree(selectedChildId);
    return root ? [root] : [];
    // buildAncestorTree is stable by design; listing it would cause unnecessary recompute
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChildId, individuals, relationships, potentialParentsMap]);

  // Custom node rendering
  const renderCustomNode = ({ nodeDatum }: any) => {
    const ind = individuals.find(i => i.id === nodeDatum.id);
    const name = nodeDatum.name;
    const dob = nodeDatum.attributes?.dob || '';
    // Progenitor lines and colors
    const lines = nodeDatum.attributes?.descendantLines || [];
    const progenitors = lines.map((line: string) => ({
      label: line,
      color: getProgenitorColor(line),
    }));
    // Main border color: first progenitor or fallback
    const mainColor = progenitors[0]?.color || '#43a047';
    
    // Background color based on relationship to home person
    const relationshipColor = getRelationshipColor(nodeDatum.id, selectedChildId || homePersonIdForDna);
    // Ghosts (hypothetical ancestors) fade with lower confidence but stay readable and clickable
    const isGhost = !!ind?.is_ghost;
    const ghostConfidence = typeof ind?.confidence_score === 'number' ? ind.confidence_score : 0.5;
    const ghostOpacity = 0.45 + 0.55 * ghostConfidence;
    // DNA summary for this progenitor
    let dnaSummary = null;
    if (ind?.is_progenitor && progenitorDnaSummary[ind.id]) {
      const { matchNames, totalCm } = progenitorDnaSummary[ind.id];
      // Human genome is ~6800 cM, so % = totalCm / 6800 * 100
      const percent = totalCm ? ((totalCm / 6800) * 100).toFixed(2) : null;
      // Limit match names to 3, show '+N more' if needed
      let matchNamesDisplay = '';
      if (matchNames.length > 3) {
        matchNamesDisplay = matchNames.slice(0, 3).join(', ') + `, +${matchNames.length - 3} more`;
      } else {
        matchNamesDisplay = matchNames.join(', ');
      }
      dnaSummary = (
        <div className="family-tree-progenitor-dna-summary">
          <div className="family-tree-progenitor-dna-matches"><strong>DNA Matches:</strong> {matchNames.length > 0 ? matchNamesDisplay : 'None'}</div>
          <div className="family-tree-progenitor-dna-percent"><strong>Total Matched DNA:</strong> {percent ? `${percent}%` : '0%'} ({totalCm.toFixed(1)} cM)</div>
        </div>
      );
    }
    return (
      <g onClick={() => handleNodeClick(nodeDatum)} style={{ cursor: 'pointer' }}>
        <foreignObject x="-120" y="-80" width="240" height="220">
          <div 
            className={`family-tree-individual-box ${relationshipColor !== '#e0e0e0' ? 'has-relationship-bg' : ''} ${isGhost ? 'is-ghost' : ''}`}
            style={{
              borderColor: mainColor,
              backgroundColor: relationshipColor,
              '--relationship-bg-color': relationshipColor,
              ...(isGhost ? { opacity: ghostOpacity } : {}),
            } as React.CSSProperties}
            title={isGhost ? `Ghost (hypothetical) · ${Math.round(ghostConfidence * 100)}% confidence` : undefined}
          >
            <div className="individual-main">
              <div className="individual-name">{name}</div>
              <div className="individual-date">{dob}</div>
              {isGhost && (
                <div className="individual-ghost-badge"><span role="img" aria-label="Ghost">👻</span> {Math.round(ghostConfidence * 100)}% confidence</div>
              )}
            </div>
            <div className="progenitor-circles">
              {progenitors.map((p: { label: string, color: string }) => (
                <div
                  className="progenitor-circle"
                  style={{ background: p.color }}
                  title={p.label}
                  key={p.label}
                  tabIndex={0}
                  aria-label={p.label}
                />
              ))}
            </div>
            {/* Other parents indicator */}
            {nodeDatum.attributes?.hasOtherParents && (
              <div className="other-parents-indicator" title={`${nodeDatum.attributes.otherParentCount} other parent(s) (adoptive, step, etc.)`}>
                <span role="img" aria-label="Other parents">👥</span>
              </div>
            )}
            {dnaSummary}
          </div>
        </foreignObject>
      </g>
    );
  };

  // On node click, open modal for any person
  const handleNodeClick = (nodeDatum: any) => {
    const ind = individuals.find(i => i.id === nodeDatum.id);
    if (!ind) return;
    if (ind.is_progenitor) {
      // Only open DNA modal for progenitors
      setPersonModalOpen(false);
      setPersonModalData(null);
      setSelectedProgenitorId(ind.id);
      setDnaMatchModalOpen(true);
      setDnaMatchLoading(true);
      setDnaMatchError(null);
      setDnaMatchData(null);
      // Fetch from backend
      fetch(`${API_URL}/progenitor/${ind.id}/dna_matches?home_id=${encodeURIComponent(selectedChildId || homePersonIdForDna)}`)
        .then(res => {
          if (!res.ok) throw new Error('Failed to fetch DNA matches');
          return res.json();
        })
        .then(data => {
          setDnaMatchData(data);
          setDnaMatchLoading(false);
        })
        .catch(err => {
          setDnaMatchError(err.message);
          setDnaMatchLoading(false);
        });
    } else {
      setPersonModalData(ind);
      setPersonModalOpen(true);
    }
  };

  // After a merge the edited record is gone: close everything and reload
  const handleMerged = () => {
    setModalOpen(false);
    setEditIndividualModalOpen(false);
    setEditIndividual(null);
    setPersonModalOpen(false);
    fetchIndividuals();
    fetchRelationships();
  };

  // Open modal for add
  const handleAddIndividual = () => {
    setDuplicateCandidates([]);
    setModalMode('add');
    setModalIndividual({});
    setModalOpen(true);
  };

  // Save handler
  const handleSaveIndividual = async (
    ind: any,
    bioFatherId: string | null,
    bioMotherId: string | null,
    spouseId: string | null,
    adoptiveFatherId: string | null,
    adoptiveMotherId: string | null,
    allowDuplicate: boolean = false,
  ) => {
    const payload = {
      ...ind,
      is_ghost: !!ind.is_ghost,
    };
    let savedId = ind.id;
    if (modalMode === 'add') {
      setDuplicateCandidates([]);
      const res = await fetch(`${API_URL}/individuals${allowDuplicate ? '?allow_duplicate=true' : ''}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.status === 409) {
        pendingSaveRef.current = [ind, bioFatherId, bioMotherId, spouseId, adoptiveFatherId, adoptiveMotherId, true];
        setDuplicateCandidates(data.candidates || []);
        return;
      }
      savedId = data.id;
    } else {
      await fetch(`${API_URL}/individuals/${ind.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    }
    const syncParentEdge = async (relType: string, parentId: string | null) => {
      const existing = relationships.filter(r => r.to_id === savedId && r.type === relType);
      for (const r of existing) {
        await fetch(`${API_URL}/relationships/${r.id}`, { method: 'DELETE' });
      }
      if (parentId && parentId !== savedId) {
        await fetch(`${API_URL}/relationships`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ from_id: parentId, to_id: savedId, type: relType }),
        });
      }
    };
    await syncParentEdge('Bio Father', bioFatherId);
    await syncParentEdge('Bio Mother', bioMotherId);
    await syncParentEdge('Adoptive Father', adoptiveFatherId);
    await syncParentEdge('Adoptive Mother', adoptiveMotherId);
    // Create spouse relationship if needed
    if (spouseId && spouseId !== savedId) {
      // Check if already exists
      const exists = relationships.some(r => ((r.from_id === savedId && r.to_id === spouseId) || (r.from_id === spouseId && r.to_id === savedId)) && r.type === 'Spouse');
      if (!exists) {
        await fetch(`${API_URL}/relationships`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ from_id: savedId, to_id: spouseId, type: 'Spouse' }),
        });
      }
    }
    setModalOpen(false);
    setEditIndividualModalOpen(false);
    setEditIndividual(null);
    setPersonModalOpen(false);
    fetchIndividuals();
    fetchRelationships();
  };

  useEffect(() => {
    if (personModalData && personModalOpen) {
      fetch(`${API_URL}/individuals/${personModalData.id}/potential_parents`).then(r => r.json()).then(setPotentialParents);
      fetch(`${API_URL}/individuals/${personModalData.id}/potential_children`).then(r => r.json()).then(setPotentialChildren);
    }
  }, [personModalData, personModalOpen]);

  // Custom path function for cleaner connector lines
  const customPathFunc = (linkData: any, orientation: string) => {
    const { source, target } = linkData;
    const x0 = source.x, y0 = source.y, x1 = target.x, y1 = target.y;
    
    // Elbow from parent to child, turning halfway between generations.
    // In horizontal layouts react-d3-tree draws a node's x on the vertical axis.
    const midDepth = (y0 + y1) / 2;
    if (orientation === 'horizontal') {
      return `M${y0},${x0}H${midDepth}V${x1}H${y1}`;
    }
    return `M${x0},${y0}V${midDepth}H${x1}V${y1}`;
  };

  // Handler for rebuilding progenitor index
  const handleRebuildProgenitorIndex = async () => {
    setRebuildLoading(true);
    setRebuildMessage(null);
    try {
      await fetch(`${API_URL}/progenitor_index/rebuild`, { method: 'POST' });
      // Refetch the index
      const res = await fetch(`${API_URL}/progenitor_index`);
      const data = await res.json();
      setProgenitorIndex(data);
      setRebuildMessage('Progenitor index rebuilt!');
      setTimeout(() => setRebuildMessage(null), 2500);
    } catch (e) {
      setRebuildMessage('Failed to rebuild progenitor index');
      setTimeout(() => setRebuildMessage(null), 3500);
    } finally {
      setRebuildLoading(false);
    }
  };

  // Dynamically center tree on layout or window resize
  useEffect(() => {
    function updateTranslate() {
      if (treeContainerRef.current) {
        const rect = treeContainerRef.current.getBoundingClientRect();
        if (treeLayout === 'horizontal') {
          // Starting person on the left, ancestors to the right
          setTreeTranslate({ x: 160, y: rect.height / 2 });
        } else {
          setTreeTranslate({ x: rect.width / 2, y: 120 });
        }
      }
    }
    updateTranslate();
    window.addEventListener('resize', updateTranslate);
    return () => window.removeEventListener('resize', updateTranslate);
  }, [treeLayout]);

  // Zoom controls
  const handleZoomIn = () => setTreeZoom(z => Math.min(z + 0.2, 2.5));
  const handleZoomOut = () => setTreeZoom(z => Math.max(z - 0.2, 0.4));
  const handleZoomReset = () => setTreeZoom(1);

  // Separation multiplies the node slot, so keep it near 1: a slot already fits
  // one 240px-wide person box with a small gap
  const getSeparation = () => ({ siblings: 1, nonSiblings: 1.15 });
  const getNodeSize = () => {
    // nodeSize.x is always horizontal distance: between generations when horizontal,
    // between relatives when vertical
    if (treeLayout === 'vertical') return { x: 260, y: 230 };
    return { x: 290, y: 175 };
  };

  return (
    <section className="genealogy-section">
      {/* Floating Add Individual Button and Rebuild Button */}
      <div className="family-tree-floating-btn-group">
        <button className="genealogy-btn-primary" onClick={handleAddIndividual}>Add Individual</button>
        <button className="genealogy-btn-secondary" onClick={handleRebuildProgenitorIndex} disabled={rebuildLoading} style={{ minWidth: 180 }}>
          {rebuildLoading ? 'Rebuilding...' : 'Rebuild Progenitor Index'}
        </button>
        {rebuildMessage && <div style={{ color: rebuildMessage.includes('Failed') ? 'var(--color-error)' : 'var(--color-success)', fontWeight: 600, marginTop: 4 }}>{rebuildMessage}</div>}
      </div>
      <div className="genealogy-family-tree-title-row">
        <h1 className="heading-1">Family Tree Visualisation</h1>
      </div>
      <div className="genealogy-tree-controls genealogy-tree-controls-advanced">
        <div className="family-tree-layout-toggle genealogy-tree-layout-toggle">
          <span className="family-tree-layout-label genealogy-tree-layout-label">Layout:</span>
          <button
            className={`family-tree-layout-btn genealogy-tree-layout-btn${treeLayout === 'horizontal' ? ' genealogy-tree-layout-btn-active' : ''}`}
            onClick={() => setTreeLayout('horizontal')}
            aria-label="Horizontal Layout"
          >Horizontal</button>
          <button
            className={`family-tree-layout-btn genealogy-tree-layout-btn${treeLayout === 'vertical' ? ' genealogy-tree-layout-btn-active' : ''}`}
            onClick={() => setTreeLayout('vertical')}
            aria-label="Vertical Layout"
          >Vertical</button>
          <button
            className={`family-tree-layout-btn genealogy-tree-layout-btn${treeLayout === 'fan' ? ' genealogy-tree-layout-btn-active' : ''}`}
            onClick={() => setTreeLayout('fan')}
            aria-label="Fan Layout"
          >Fan</button>
        </div>
        <div className="genealogy-tree-zoom-controls">
          <button className="genealogy-tree-zoom-btn" onClick={handleZoomOut} aria-label="Zoom Out">-</button>
          <button className="genealogy-tree-zoom-btn" onClick={handleZoomReset} aria-label="Reset Zoom">Reset</button>
          <button className="genealogy-tree-zoom-btn" onClick={handleZoomIn} aria-label="Zoom In">+</button>
        </div>
      </div>
      <div className="tree-controls">
        <label>
          Select Starting Person:
          <select value={selectedChildId} onChange={e => setSelectedChildId(e.target.value)} className="add-person-modal-input">
            <option value="">Select Individual</option>
            {individuals.map(ind => (
              <option key={ind.id} value={ind.id}>{[ind.first_name, ind.middle_names, ind.surname].filter(Boolean).join(' ')}</option>
            ))}
          </select>
        </label>
      </div>
      {selectedChildId && treeData && (
        <div
          className="genealogy-tree-visualization-container genealogy-tree-visualization-responsive"
          ref={treeContainerRef}
          style={{ width: '100%', height: '80vh', minHeight: 500, background: '#23272f', borderRadius: '12px', boxShadow: '0 4px 24px rgba(0,0,0,0.13)', marginTop: '2rem', overflow: 'auto', position: 'relative' }}
        >
          {treeLayout === 'fan' ? (
            treeData[0] && (
              <div className="fan-chart-container">
                <FanChart
                  root={treeData[0]}
                  zoom={treeZoom}
                  fillFor={(id) => getRelationshipColor(id, selectedChildId)}
                  ghostConfidence={(id) => {
                    const ind = individuals.find(i => i.id === id);
                    return typeof ind?.confidence_score === 'number' ? ind.confidence_score : 0.5;
                  }}
                  onSelect={handleNodeClick}
                />
              </div>
            )
          ) : (
          <Tree
            data={treeData}
            orientation={treeLayout}
            translate={treeTranslate}
            zoom={treeZoom}
            renderCustomNodeElement={renderCustomNode}
            pathFunc={customPathFunc}
            zoomable
            collapsible={false}
            separation={getSeparation()}
            nodeSize={getNodeSize()}
            enableLegacyTransitions={false}
            shouldCollapseNeighborNodes={false}
            onNodeClick={handleNodeClick}
          />
          )}
        </div>
      )}
      
      {/* DNA Match Modal */}
      {dnaMatchModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content genealogy-card dna-match-modal-centered">
            <h2 className="heading-2">DNA Matches for Progenitor</h2>
            {selectedProgenitorId && (
              <button
                className="genealogy-btn-primary"
                style={{ margin: '0 0 24px 0', alignSelf: 'flex-start' }}
                onClick={() => {
                  const ind = individuals.find(i => i.id === selectedProgenitorId);
                  setDnaMatchModalOpen(false);
                  setPersonModalData(ind || null);
                  setPersonModalOpen(true);
                }}
                aria-label="View Individual Details"
              >View Individual Details</button>
            )}
            <button className="genealogy-btn-secondary" style={{ position: 'absolute', top: 24, right: 24 }} onClick={() => setDnaMatchModalOpen(false)} aria-label="Close DNA Modal">Close</button>
            {dnaMatchLoading && <p>Loading...</p>}
            {dnaMatchError && <p style={{ color: 'red' }}>{dnaMatchError}</p>}
            {dnaMatchData && (
              <>
                <label className="genealogy-dna-modal-filter-label" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, marginBottom: 8 }}>
                  <input
                    type="checkbox"
                    checked={dnaModalShowOnlyMultiPerson}
                    onChange={(e) => setDnaModalShowOnlyMultiPerson(e.target.checked)}
                    aria-label="Show only segments shared by 2 or more people"
                  />
                  <span>Show only segments shared by 2+ people</span>
                </label>
              <div className="dna-modal-columns" style={{ display: 'flex', gap: 40, alignItems: 'flex-start', flexWrap: 'wrap', marginTop: 24 }}>
                {/* Fallback for no data */}
                {((!dnaMatchData.matches || dnaMatchData.matches.length === 0) && (!dnaMatchData.shared_segments || dnaMatchData.shared_segments.length === 0) && (!dnaMatchData.descendants || dnaMatchData.descendants.length === 0 || dnaMatchData.descendants.every((d: any) => d.num_segments === 0))) && (
                  <div style={{ width: '100%', textAlign: 'center', color: 'var(--color-text-main)', fontSize: '1.2em', padding: '48px 0' }}>
                    <p>No DNA data found for this progenitor or their matches.<br/>Try editing a match and pasting DNA data, then try again.</p>
                  </div>
                )}
                {/* Left: Ancestor Path and Matches */}
                {((dnaMatchData.matches && dnaMatchData.matches.length > 0) || (dnaMatchData.shared_segments && dnaMatchData.shared_segments.length > 0)) && (
                <>
                {!dnaModalShowOnlyMultiPerson && (
                <div style={{ flex: 1, minWidth: 340, marginBottom: 32 }}>
                  <h3 className="heading-3">DNA Matches</h3>
                  {dnaMatchData.matches.length === 0 && <p>No DNA matches found for this progenitor.</p>}
                  {dnaMatchData.matches.map((m: any) => (
                    <div key={m.match_id} className="genealogy-card" style={{ marginBottom: 18, padding: 10 }}>
                      <div style={{ fontWeight: 600, color: 'var(--color-text-main)' }}>{m.match_name}</div>
                      <div style={{ fontSize: '0.95em', color: '#666' }}>Segments:</div>
                      <div className="dna-modal-table-container" style={{ overflowX: 'auto' }}>
                        <table>
                          <thead>
                            <tr>
                              <th>Chr</th><th>Start</th><th>End</th><th>cM</th><th>SNPs</th><th>Start RSID</th><th>End RSID</th>
                            </tr>
                          </thead>
                          <tbody>
                            {m.segments.map((seg: any, i: number) => (
                              <tr key={i}>
                                <td>{seg.chromosome}</td>
                                <td>{seg.start_location}</td>
                                <td>{seg.end_location}</td>
                                <td>{seg.centimorgans}</td>
                                <td>{seg.snps}</td>
                                <td>{seg.start_rsid}</td>
                                <td>{seg.end_rsid}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
                )}
                <div style={{ flex: 1.2, minWidth: 400, marginBottom: 32 }}>
                  <h3 className="heading-3">Shared DNA Segments (2+ people)</h3>
                  <div className="dna-modal-table-container" style={{ overflowX: 'auto' }}>
                    <table>
                      <thead>
                        <tr>
                          <th>Chr</th><th>Start</th><th>End</th><th>Matches</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(dnaMatchData.shared_segments || []).map((seg: any, i: number) => (
                          <tr key={i}>
                            <td>{seg.chromosome}</td>
                            <td>{seg.start}</td>
                            <td>{seg.end}</td>
                            <td>
                              {seg.match_ids.map((id: string) => {
                                const match = dnaMatchData.matches.find((m: any) => m.match_id === id);
                                return match ? match.match_name : id;
                              }).join(', ')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {!dnaModalShowOnlyMultiPerson && (
                  <>
                  <hr style={{ margin: '32px 0', border: 0, borderTop: '1px solid #444' }} />
                  <h3 className="heading-3">Individual Matched DNA</h3>
                  {dnaMatchData.matches.map((match: any, i: number) => (
                    <div key={match.match_id} className="genealogy-card" style={{ marginBottom: 16 }}>
                      <strong>{match.match_name}</strong>
                      <div className="dna-modal-table-container" style={{ overflowX: 'auto' }}>
                        <table>
                          <thead>
                            <tr>
                              <th>Chr</th><th>Start</th><th>End</th>
                            </tr>
                          </thead>
                          <tbody>
                            {((dnaMatchData.individual_segments && dnaMatchData.individual_segments[match.match_id]) || []).map((seg: any, i: number) => (
                              <tr key={i}>
                                <td>{seg.chromosome}</td>
                                <td>{seg.start}</td>
                                <td>{seg.end}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </>
                  )}
                </div>
                </>
                )}
              </div>
              </>
            )}
          </div>
        </div>
      )}
      {/* Person Modal (on node click) */}
      {personModalOpen && personModalData && (
        <div className="modal-overlay">
          <div className="modal-content genealogy-card" style={{ maxWidth: 400 }}>
            <h3 className="heading-3">Individual Details</h3>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              <li><strong>Name:</strong> {[personModalData.first_name, personModalData.middle_names, personModalData.surname].filter(Boolean).join(' ')}{personModalData.is_ghost ? ' 👻' : ''}</li>
              <li><strong>Gender:</strong> {
                ({ male: 'Male', M: 'Male', female: 'Female', F: 'Female', unknown: 'Unknown', U: 'Unknown' } as Record<string, string>)[personModalData.gender || ''] ?? personModalData.gender ?? 'Unknown'
              }</li>
              <li><strong>Date of Birth:</strong> {personModalData.dob}</li>
              <li><strong>Status:</strong> {personModalData.status}</li>
              {personModalData.status === 'Deceased' && (
                <li><strong>Date of Death:</strong> {personModalData.death_dob}</li>
              )}
              <li><strong>Progenitor:</strong> {personModalData.is_progenitor ? 'Yes' : 'No'}</li>
              {personModalData.is_progenitor && (
                <li><strong>Progenitor Line Name:</strong> {personModalData.progenitor_line_name}</li>
              )}
              <li><strong>Ghost:</strong> {personModalData.is_ghost ? 'Yes 👻' : 'No'}</li>
              <li><strong>Potential Parents:</strong> {potentialParents.length === 0 ? '-' : potentialParents.map((p: any, i: number) => (
                <span key={p.individual.id}>{[p.individual.first_name, p.individual.middle_names, p.individual.surname].filter(Boolean).join(' ') || 'Unknown'}{p.individual.is_ghost ? ' 👻' : ''}{i < potentialParents.length - 1 ? ', ' : ''}</span>
              ))}</li>
              <li><strong>Potential Children:</strong> {potentialChildren.length === 0 ? '-' : potentialChildren.map((c: any, i: number) => (
                <span key={c.individual.id}>{[c.individual.first_name, c.individual.middle_names, c.individual.surname].filter(Boolean).join(' ') || 'Unknown'}{c.individual.is_ghost ? ' 👻' : ''}{i < potentialChildren.length - 1 ? ', ' : ''}</span>
              ))}</li>
            </ul>
            <button
              className="genealogy-btn-primary"
              style={{ marginRight: 8 }}
              onClick={() => {
                setEditIndividual({ ...personModalData });
                setEditIndividualModalOpen(true);
                setPersonModalOpen(false);
              }}
            >Edit</button>
            <button
              className="genealogy-btn-secondary"
              style={{ marginLeft: 8 }}
              onClick={() => setPersonModalOpen(false)}
            >Close</button>
          </div>
        </div>
      )}
      {/* Edit Individual Modal */}
      {editIndividualModalOpen && editIndividual && (
        <IndividualModal
          open={editIndividualModalOpen}
          mode="edit"
          individual={editIndividual || {}}
          allIndividuals={individuals}
          allRelationships={relationships}
          onSave={handleSaveIndividual}
          onCancel={() => {
            setEditIndividualModalOpen(false);
            setEditIndividual(null);
            setPersonModalOpen(false);
          }}
          loading={false}
          onMerged={handleMerged}
        />
      )}
      <IndividualModal
        open={modalOpen}
        mode={modalMode}
        individual={modalIndividual}
        allIndividuals={individuals}
        allRelationships={relationships}
        onSave={handleSaveIndividual}
        onCancel={() => setModalOpen(false)}
        loading={false}
        duplicateCandidates={duplicateCandidates}
        onCreateAnyway={() => { if (pendingSaveRef.current) handleSaveIndividual(...pendingSaveRef.current); }}
        onOpenExisting={(id) => {
          const existing = individuals.find(i => i.id === id);
          if (!existing) return;
          setModalOpen(false);
          setEditIndividual(existing);
          setEditIndividualModalOpen(true);
        }}
        onMerged={handleMerged}
      />
    </section>
  );
};

export default FamilyTreePage; 